import { Ref } from "react";
import { HTTP_GET, HTTP_POST, HTTP_PUT, HTTP_DELETE, WebApiRequest } from "tools/webservice";
import { ProjectRequest, ProjectResponse, TopicRequest, TopicResponse, TopicPromoteRequest, MarkdownResourceRequest, MarkdownResourceResponse, TopicMarkupResponse, TopicMarkdownResponse, SimpleResult } from "./interfaces";

export const createProject = (request: ProjectRequest, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_POST,
    endpoint: "/content-library/project",
    payload: request,
    successCallback,
    mountedRef
  });

export const retrieveProject = (projectId: string, successCallback: (resp: ProjectResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/project/${projectId}`,
    successCallback,
    mountedRef
  });

export const updateProject = (projectId: string, request: ProjectRequest, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_PUT,
    endpoint: `/content-library/project/${projectId}`,
    payload: request,
    successCallback,
    mountedRef
  });

export const deleteProject = (projectId: string, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_DELETE,
    endpoint: `/content-library/project/${projectId}`,
    successCallback,
    mountedRef
  });

export const getProjects = (successCallback: (resp: ProjectResponse[]) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: "/content-library/projects",
    successCallback,
    mountedRef
  });

export const createTopic = (projectId: string, request: TopicRequest, successCallback: (response: TopicResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_POST,
    endpoint: `/content-library/project/${projectId}/topic`,
    payload: request,
    successCallback,
    mountedRef
  });

export const createTopicWithId = (projectId: string, topicId: string, request: TopicRequest, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_POST,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}`,
    payload: request,
    successCallback,
    mountedRef
  });

export const retrieveTopic = (projectId: string, topicId: string, successCallback: (resp: TopicResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}`,
    successCallback,
    mountedRef
  });

export const retrieveTopicMarkdown = (projectId: string, topicId: string, successCallback: (markdown: TopicMarkdownResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}/markdown`,
    successCallback,
    mountedRef
  });

export const retrieveTopicMarkup = (topicId: string, successCallback: (markup: TopicMarkupResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/topic/${topicId}/markup`,
    successCallback,
    mountedRef
  });

export const compileMarkupPreview = (projectId: string, markdown: string, successCallback: (preview: TopicMarkupResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_POST,
    endpoint: `/content-library/project/${projectId}/topic/markup`,
    payload: { topicMarkdown: markdown },
    successCallback,
    mountedRef
  });

export const updateTopic = (projectId: string, topicId: string, request: TopicRequest, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_PUT,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}`,
    payload: request,
    successCallback,
    mountedRef
  });

export const updateTopicMarkdown = (projectId: string, topicId: string, markdown: string, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_PUT,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}/markdown`,
    payload: { topicMarkdown: markdown },
    successCallback,
    mountedRef
  });

export const deleteTopic = (projectId: string, topicId: string, successCallback: (resp: SimpleResult) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_DELETE,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}`,
    successCallback,
    mountedRef,
    disableDefaultSuccessResponseHandler: true,
  });

export const getTopicsList = (projectId: string, successCallback: (resp: TopicResponse[]) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/project/${projectId}/topics`,
    successCallback,
    mountedRef
  });

export const promoteTopic = (projectId: string, topicId: string, request: TopicPromoteRequest, successCallback: () => void, errorCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_PUT,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}/promote`,
    payload: request,
    successCallback,
    errorCallback,
    mountedRef
  });

export const createMarkdownResource = (request: MarkdownResourceRequest, successCallback: (resp: MarkdownResourceResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_POST,
    endpoint: "/content-library/resource",
    payload: request,
    successCallback,
    mountedRef
  });

export const createMarkdownResourceWithId = (resourceId: string, request: MarkdownResourceRequest, successCallback: (resp: MarkdownResourceResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_POST,
    endpoint: `/content-library/resource/${resourceId}`,
    payload: request,
    successCallback,
    mountedRef
  });

export const retrieveMarkdownResource = (resourceId: string, successCallback: (resp: MarkdownResourceResponse) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/resource/${resourceId}`,
    successCallback,
    mountedRef
  });

export const updateMarkdownResource = (resourceId: string, request: MarkdownResourceRequest, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_PUT,
    endpoint: `/content-library/resource/${resourceId}`,
    payload: request,
    successCallback,
    mountedRef
  });

export const deleteMarkdownResource = (resourceId: string, successCallback: (resp: SimpleResult) => void, mountedRef: Ref<boolean>) =>
  WebApiRequest({
    method: HTTP_DELETE,
    endpoint: `/content-library/resource/${resourceId}`,
    successCallback,
    mountedRef,
    disableDefaultSuccessResponseHandler: true
  });

export const getMarkdownResources = (successCallback: (resp: MarkdownResourceResponse[]) => void, mountedRef: Ref<boolean>) =>
  WebApiRequest({
    method: HTTP_GET,
    endpoint: "/content-library/resources",
    successCallback,
    mountedRef
  });

export const updateMarkdownResourceFile = (resourceId: string, arrayBuffer: any, fileType: string, successCallback: () => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_PUT,
    endpoint: `/content-library/resource/${resourceId}/file`,
    arrayBuffer: arrayBuffer,
    arrayBufferContentType: fileType,
    successCallback,
    mountedRef
  });

export const retrieveMarkdownResourceFile = (resourceId: string, successCallback: (fileData: any) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/resource/${resourceId}/file`,
    successCallback,
    mountedRef
  });

export const listFormsUsingTopic = (projectId: string, topicId: string, successCallback: (formIds: string[]) => void, mountedRef: Ref<boolean>) => 
  WebApiRequest({
    method: HTTP_GET,
    endpoint: `/content-library/project/${projectId}/topic/${topicId}/formslist`,
    successCallback,
    mountedRef
  });
